import { CdkDragEnd } from '@angular/cdk/drag-drop'
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { Select, Store } from '@ngxs/store'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject, of } from 'rxjs'
import { filter, startWith, last, map, catchError, takeUntil } from 'rxjs/operators'

import { AppState, DeleteFormLibraryPiece, GetTemplateLibraryPieces, UpdatePiece } from 'src/app/app.state'
import { Constants, ERROR_MESSAGES, FORM_LIBRARY_DROP_TYPE, i18nParams, MENU_OPTION_KEY, NOTIFICATION_TYPES } from 'src/app/constants/internationalized-constants-en'
import { equalsIgnoreCase, trimAll } from 'src/app/utils/utils'
import { DialogComponent } from '../dialog/dialog.component'
import { MenuComponent } from '../menu/menu.component'
import { MenuOption } from '../menu/menu.component'
import { NotificationSnackbarService } from '../notification-snackbar/services/notification-snackbar.service'
import { PieceEditDialogComponent } from '../piece-edit-dialog/piece-edit-dialog.component'
import { TemplateLibraryPieceModel } from '../template-library/models/template-library.model'
import { CheckDuplicatePieceNameService } from '../template-library/services/check-duplicate-piece-name/check-duplicate-piece-name.service'
import { KeyboardFormLibraryDragData, KeyboardFormLibraryDragService } from './services/keyboard-form-library-drag/keyboard-form-library-drag.service'

@Component({
  selector: 'app-form-library',
  templateUrl: './form-library.component.html',
  styleUrls: ['./form-library.component.scss']
})
export class FormLibraryComponent implements OnInit, OnDestroy {
  @Select(AppState.getFormLibraryPieces) formLibraryPieces$: Observable<TemplateLibraryPieceModel[]>
  @Select(AppState.getBlankFormLibraryPieces) _blankFormLibraryPieces$: Observable<TemplateLibraryPieceModel[]>
  @Select(AppState.getAccessStatus) isAccessGranted$: Observable<boolean>

  @Output() draggedFormField = new EventEmitter<boolean>()

  NO_PREMADE_FIELD_DATA_MESSAGE = ERROR_MESSAGES.NO_PREMADE_FIELD_DATA
  menuOptions: MenuOption[] = [
    // ECT2-1326: Set disable true to temporarily disable this option
    // EVIRPX-5: Need to make code changes to ensure the Edit feature works correctly when this option is enabled
    { text: MENU_OPTION_KEY.EDIT, disable: true },
    { text: MENU_OPTION_KEY.DELETE },
  ]
  formLibraryDropType = FORM_LIBRARY_DROP_TYPE
  draggedDestination = 'inspection-type-detail-row-list'

  blankFormLibraryPieces$: Observable<TemplateLibraryPieceModel[]>
  private destroy$ = new Subject<void>()

  public shouldLoading$: Observable<any> = of(false)

  constructor(
    public store: Store,
    public dialog: MatDialog,
    public snackbarService: NotificationSnackbarService,
    public pieceNameService: CheckDuplicatePieceNameService,
    public translateService: TranslateService,
    private keyboardFormLibraryDragService: KeyboardFormLibraryDragService,
  ) {
    this.blankFormLibraryPieces$ = this._blankFormLibraryPieces$.pipe(
      map(blankPieces => blankPieces.map(blankPiece => ({ ...blankPiece, name: this.translateService.instant(blankPiece.name) }))),
    )
  }

  ngOnInit(): void {
    this.keyboardFormLibraryDragService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (!state.active) {
        this.draggedFormField.emit(false)
      }
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onDragEnded(event: CdkDragEnd) {
    this.draggedFormField.emit(false)

    // Return focus to the grabbed library field after drop or Escape cancel.
    if (event.source && event.source.element) {
      setTimeout(() => event.source.element.nativeElement.focus(), 0)
    }
  }

  onLibraryFieldKeydown(event: KeyboardEvent, dragData: KeyboardFormLibraryDragData, sourceEl: HTMLElement) {
    if (event.target !== event.currentTarget) {
      return
    }

    const key = this.normalizeKeyboardKey(event.key)
    const dragState = this.keyboardFormLibraryDragService.snapshot

    if (key === 'Escape' && dragState.active) {
      event.preventDefault()
      event.stopPropagation()
      this.draggedFormField.emit(false)
      this.keyboardFormLibraryDragService.cancel()
      return
    }

    if (key === 'Enter' || key === ' ') {
      event.preventDefault()
      event.stopPropagation()

      if (!dragState.active) {
        this.draggedFormField.emit(true)
        this.keyboardFormLibraryDragService.start(dragData, sourceEl)
      }
      return
    }

    if (key === 'ArrowLeft' && dragState.active) {
      event.preventDefault()
      event.stopPropagation()
      this.keyboardFormLibraryDragService.requestDestinationFocus()
    }
  }

  isKeyboardGrabbed(dropType: string, dropValue: any): boolean {
    const dragState = this.keyboardFormLibraryDragService.snapshot
    return !!(dragState.active
      && dragState.dragData
      && dragState.dragData.dropType === dropType
      && dragState.dragData.dropValue === dropValue)
  }

  private normalizeKeyboardKey(key: string): string {
    if (key === 'Spacebar') {
      return ' '
    }

    return key
  }

  handleOptionSelected(menuOption: string, formPiece: TemplateLibraryPieceModel, index: number) {
    switch (menuOption) {
      case MENU_OPTION_KEY.DELETE:
        this.openDeleteConfirmDialog(formPiece, index)
        break

      case MENU_OPTION_KEY.EDIT:
        this.openPremadeFieldEditDialog(formPiece)
        break

      default:
        break
    }
  }

  openDeleteConfirmDialog(formPiece: TemplateLibraryPieceModel, deletedIndex: number) {
    let accepted = false

    this.dialog.open(DialogComponent, {
      data: {
        onClose: () => {
          if (!accepted) {
            this.restoreMenuFocus(deletedIndex)
          }
        },
        onAccept: () => {
          accepted = true
          this.deleteFormLibraryPiece(formPiece, deletedIndex)
        },
        cancelText: this.translateService.instant('CANCEL'),
        acceptText: this.translateService.instant('YES'),
        title: this.translateService.instant('INSPECTION_PIECE_DELETE_CONFIRM_TITLE', { name: formPiece.name }),
        focusAccept: true,
      },
      panelClass: 'delete-piece-dialog'
    })
  }

  deleteFormLibraryPiece(formPiece: TemplateLibraryPieceModel, deletedIndex: number) {
    const currentPieces = this.store.selectSnapshot(AppState.getFormLibraryPieces) || []
    const targetFocusIndex = currentPieces.length > 1
      ? Math.min(deletedIndex, currentPieces.length - 2)
      : -1

    this.store.dispatch(new DeleteFormLibraryPiece(formPiece.id)).subscribe(
      value => { },
      error => {
        const detailedError = {
          'Error': [`${this.translateService.instant('ERROR_CODE')}: ${error.status}`]
        }
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('INSPECTION_PIECE_DELETE_FAIL_MSG', { name: formPiece.name }),
          NOTIFICATION_TYPES.FAILED,
          detailedError
        )

        this.restoreMenuFocus(deletedIndex)
      },
      () => {
        this.pieceNameService.removePieceName(formPiece.name, formPiece.id)
        const reload$ = this.store.dispatch(new GetTemplateLibraryPieces()).pipe(
          last(),
          map(() => false),
          startWith(true),
          catchError(() => of(false))
        )
        this.shouldLoading$ = reload$

        if (targetFocusIndex >= 0) {
          reload$.pipe(last()).subscribe(() => {
            this.focusEllipsisByIndex(targetFocusIndex)
          })
        }

        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('INSPECTION_PIECE_DELETE_SUCCESS_MSG', i18nParams.FORMAT_PIECE_NAME(formPiece.name)),
          NOTIFICATION_TYPES.SUCCESS,
          null,
          Constants.snackbar.duration
        )
      }
    )
  }

  private restoreMenuFocus(fallbackIndex: number) {
    const lastTrigger = MenuComponent.lastTriggerElement
    if (lastTrigger && document.contains(lastTrigger)) {
      setTimeout(() => lastTrigger.focus(), 0)
      return
    }

    this.focusEllipsisByIndex(fallbackIndex)
  }

  private focusEllipsisByIndex(index: number) {
    if (index < 0) {
      return
    }

    setTimeout(() => {
      const ellipsisEls = document.querySelectorAll('.form-library .premade-field .more_horiz') as NodeListOf<HTMLElement>
      if (!ellipsisEls.length) {
        return
      }

      const targetIndex = Math.min(index, ellipsisEls.length - 1)
      ellipsisEls[targetIndex]?.focus()
    }, 100)
  }

  openPremadeFieldEditDialog(premadeField: TemplateLibraryPieceModel) {
    const oldPremadeFieldName = premadeField.name
    this.dialog.open(PieceEditDialogComponent, {
      data: {
        editPiece: premadeField,
        title: this.translateService.instant('INSPECTION_PIECE_EDIT_TITLE'),
      },
      panelClass: 'piece-edit-dialog',
    }).afterClosed().pipe(filter(data => !!data))
      .subscribe(([id, updatePremadeField, isAdmin]) => this.updatePremadeField(id, updatePremadeField, oldPremadeFieldName, isAdmin))
  }

  private updatePremadeField(id: string, premadeField: TemplateLibraryPieceModel, oldPremadeFieldName: string, isAdmin: boolean) {
    premadeField.name = trimAll(premadeField.name)
    this.store.dispatch(new UpdatePiece(premadeField, id, isAdmin)).subscribe(
      value => { },
      error => {
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('PIECE_EDIT_FAIL_MSG', { name: premadeField.name }),
          NOTIFICATION_TYPES.FAILED
        )
      },
      () => {
        if (!equalsIgnoreCase(oldPremadeFieldName, premadeField.name)) {
          this.pieceNameService.removePieceName(oldPremadeFieldName, id)
          this.pieceNameService.addPieceName(premadeField.name, id)
        }
        this.shouldLoading$ = this.store.dispatch(new GetTemplateLibraryPieces()).pipe(
          last(),
          map(() => false),
          startWith(true),
          catchError(() => of(false))
        )
        this.snackbarService.openNotificationSnackbar(
          this.translateService.instant('INSPECTION_PIECE_EDIT_SUCCESS_MSG', { name: premadeField.name }),
          NOTIFICATION_TYPES.SUCCESS, null, Constants.snackbar.duration
        )
      }
    )
  }
}
